import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { createSelector } from 'reselect';
import { RootState } from 'store/types';

import { getInboxMessageForSearching } from 'lib/messaging/utils';
import take from 'lodash/take';
import { objectToQsParam } from 'modules/utils/UrlUtils';
import {
  currentConsumerPcpDoctorsSelector,
  currentConsumerPastDoctorsSelector
} from 'store/myDoctors/selectors';
import { MessageStatus, MessageSummary } from './types';

const inboxMessageSearchOptions: Fuse.IFuseOptions<MessageSummary> = {
  keys: ['senderDisplayName', 'subject'],
  isCaseSensitive: false,
  location: 0,
  threshold: 0.4
};

const outboxMessageSearchOptions: Fuse.IFuseOptions<MessageSummary> = {
  keys: ['receivers.receiverDisplayName', 'subject'],
  isCaseSensitive: false,
  location: 0,
  threshold: 0.4
};

const createInboxSearchFilter = (searchText: string) => (messages: MessageSummary[]) => {
  const msgs = getInboxMessageForSearching(messages);

  if (searchText) {
    const fuseMsgs = new Fuse(msgs, inboxMessageSearchOptions).search(searchText);
    return fuseMsgs.map(msg => msg.item) as MessageSummary[];
  }
  return msgs;
};

const createOutboxSearchFilter = (searchText: string) => (messages: MessageSummary[]) => {
  if (searchText) {
    const fuseMsgs = new Fuse(messages, outboxMessageSearchOptions).search(searchText);
    return fuseMsgs.map(msg => msg.item) as MessageSummary[];
  }
  return messages;
};

const dateComparisonFn = (dateA: Date | number | string, dateB: Date | number | string) =>
  new Date(dateB).getTime() - new Date(dateA).getTime();

/**
 * Messages Inbox
 */
export const messagesInboxSelector = (state: RootState) => state.messaging.inbox;

export const inboxFetchingSelector = createSelector(
  messagesInboxSelector,
  messages => messages.isFetching
);

export const inboxErrorSelector = createSelector(messagesInboxSelector, messages => messages.error);

export const inboxDataSelector = createSelector(messagesInboxSelector, messages => messages.data);
export const inboxRequestStateSelector = createSelector(
  messagesInboxSelector,
  messages => messages.requestState
);

export const inboxSearchTextSelector = createSelector(
  messagesInboxSelector,
  messages => messages.searchText
);

export const inboxUnreadSelector = createSelector(inboxDataSelector, messages =>
  messages.filter(msg => msg.status === MessageStatus.UNREAD)
);

export const sortedInboxDataSelector = createSelector(
  [inboxDataSelector, inboxSearchTextSelector],
  (messages, searchText) => {
    const filtered = createInboxSearchFilter(searchText)(messages);

    return [...filtered].sort((a, b) => dateComparisonFn(a.createDate, b.createDate));
  }
);

/**
 * Messages Outbox
 */
export const messagesOutboxSelector = (state: RootState) => state.messaging.outbox;

export const outboxFetchingSelector = createSelector(
  messagesOutboxSelector,
  messages => messages.isFetching
);

export const outboxErrorSelector = createSelector(
  messagesOutboxSelector,
  messages => messages.error
);

export const outboxDataSelector = createSelector(messagesOutboxSelector, messages => messages.data);
export const outboxRequestStateSelector = createSelector(
  messagesOutboxSelector,
  messages => messages.requestState
);

export const outboxSearchTextSelector = createSelector(
  messagesOutboxSelector,
  messages => messages.searchText
);

export const sortedOutboxDataSelector = createSelector(
  [outboxDataSelector, outboxSearchTextSelector],
  (messages, searchText) => {
    const filtered = createOutboxSearchFilter(searchText)(messages);

    return [...filtered].sort((a, b) => dateComparisonFn(a.createDate, b.createDate));
  }
);

export const createNMostRecentMessagesSelector = (count: number) =>
  createSelector(inboxDataSelector, inbox => {
    return inbox
      .slice()
      .filter(msg => dayjs(msg.createDate).isValid())
      .sort((a, b) => (dayjs(a.createDate).isBefore(b.createDate) ? 1 : -1))
      .slice(0, count);
  });

/**
 * Message Details
 */
export const messageDetailSelector = (state: RootState) => state.messaging.detail;

export const messageDetailFetchingSelector = createSelector(
  messageDetailSelector,
  detail => detail.isFetching
);

export const messageDetailErrorSelector = createSelector(
  messageDetailSelector,
  detail => detail.error
);

export const messageDetailDataSelector = createSelector(
  messageDetailSelector,
  detail => detail.data
);

/**
 * Message Compose
 */
export const messageComposeSelector = (state: RootState) => state.messaging.compose;

export const messageComposeToSelector = createSelector(
  messageComposeSelector,
  compose => compose.to
);

export const messageComposeSendingSelector = createSelector(
  messageComposeSelector,
  compose => compose.isSending
);

export const suggestedMessagingProvidersSelector = createSelector(
  [currentConsumerPcpDoctorsSelector, currentConsumerPastDoctorsSelector],
  (pcpDoctors, pastDoctors) => {
    return take(
      [...pcpDoctors, ...pastDoctors].filter(
        doctor => doctor.messagingPools && doctor.messagingPools.length
      ),
      3
    );
  }
);

/**
 * Message Find Provider
 */

export const messageFindProviderSelector = (state: RootState) => state.messaging.findProvider;

export const messageFindProviderDataSelector = createSelector(
  messageFindProviderSelector,
  findProvider => findProvider.data
);
export const messageFindProviderIsFetchingSelector = createSelector(
  messageFindProviderSelector,
  findProvider => findProvider.isFetching
);
export const messageFindProviderErrorSelector = createSelector(
  messageFindProviderSelector,
  findProvider => findProvider.error
);

/**
 * Message Downloads
 */
export const messageDownloadsSelector = (state: RootState) => state.messaging.downloads;

/**
 * Message Client Side Caching
 */
export const inboxLastUpdatedSelector = createSelector(
  messagesInboxSelector,
  messages => messages.lastUpdated
);

export const outboxLastUpdatedSelector = createSelector(
  messagesOutboxSelector,
  messages => messages.lastUpdated
);

export const searchFormattedInbox = createSelector(inboxDataSelector, data => {
  return data.map(item => {
    return {
      title: item?.subject,
      provider: item?.senderDisplayName,
      date: item?.createDate,
      category: 'Messages',
      icon: 'Messages',
      keywords: ['messages', 'inbox'],
      data: item,
      navWebPath: `/u/messages/inbox/${item?.source}/${objectToQsParam(item?.mhpDetails)}`
    };
  });
});

export const searchFormattedOutbox = createSelector(outboxDataSelector, data => {
  return data.map(item => {
    return {
      title: item?.subject,
      provider: item?.senderDisplayName,
      date: item?.createDate,
      category: 'Messages',
      icon: 'Messages',
      keywords: ['messages', 'outbox', 'sent'],
      data: item,
      navWebPath: `/u/messages/outbox/${item?.source}/${objectToQsParam(item?.mhpDetails)}`
    };
  });
});
